import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import ProgressBar from '@/components/base/ProgressBar';
import {
  auditCategoryTone,
  auditImpactTone,
  clockOf,
  dayKeyOf,
  type AuditActorRow,
  type AuditCategoryRow,
} from '@/utils/audit';
import type { AuditEvent } from '@/types/portal';

interface AuditSummaryPanelsProps {
  categories: AuditCategoryRow[];
  total: number;
  actors: AuditActorRow[];
  highImpact: AuditEvent[];
  onSelect: (event: AuditEvent) => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  Money: 'ri-money-dollar-circle-line',
  Access: 'ri-shield-user-line',
  Campaign: 'ri-megaphone-line',
  Config: 'ri-settings-3-line',
  Security: 'ri-lock-line',
};

const TONE_PROGRESS: Record<string, 'primary' | 'accent' | 'secondary'> = {
  Money: 'accent',
  Access: 'primary',
  Campaign: 'secondary',
  Config: 'primary',
  Security: 'secondary',
};

export default function AuditSummaryPanels({
  categories,
  total,
  actors,
  highImpact,
  onSelect,
}: AuditSummaryPanelsProps) {
  const maxCategory = Math.max(1, ...categories.map((row) => row.count));

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">
              Activity by category
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">{total} events in view</p>
          </div>
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
            <i className="ri-bar-chart-line" />
          </span>
        </header>
        <ul className="divide-y divide-background-100">
          {categories.map((row) => (
            <li key={row.category} className="px-5 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 text-sm text-foreground-800">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-100 text-sm text-foreground-600">
                    <i className={CATEGORY_ICONS[row.category]} />
                  </span>
                  {row.category}
                </span>
                <span className="text-sm font-medium text-foreground-950">{row.count}</span>
              </div>
              <ProgressBar
                className="mt-2"
                value={(row.count / maxCategory) * 100}
                tone={TONE_PROGRESS[row.category]}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">Most active users</h3>
            <p className="mt-0.5 text-xs text-foreground-500">Events recorded in view</p>
          </div>
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-base text-accent-700">
            <i className="ri-user-star-line" />
          </span>
        </header>
        <ul className="divide-y divide-background-100">
          {actors.length === 0 ? (
            <li className="px-5 py-4 text-sm text-foreground-500">No activity in this view.</li>
          ) : (
            actors.map((actor) => (
              <li key={actor.id} className="flex items-center gap-3 px-5 py-3">
                <Avatar src={actor.avatar} alt={actor.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground-900">{actor.name}</p>
                  <p className="truncate text-[11px] text-foreground-500">{actor.role}</p>
                </div>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-medium text-foreground-950">{actor.count}</span>
                  <span className="block text-[10px] text-foreground-400">events</span>
                </span>
              </li>
            ))
          )}
        </ul>
      </section>

      <section className="rounded-lg border border-primary-200 bg-primary-50">
        <header className="flex items-center gap-2 border-b border-primary-200 px-5 py-3.5">
          <i className="ri-alert-line text-primary-700" />
          <h3 className="font-heading text-sm font-semibold text-foreground-950">High-impact actions</h3>
        </header>
        <ul className="divide-y divide-primary-100">
          {highImpact.length === 0 ? (
            <li className="px-5 py-4 text-sm text-foreground-600">
              No sensitive or critical actions in this view.
            </li>
          ) : (
            highImpact.slice(0, 6).map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => onSelect(event)}
                  className="w-full px-5 py-3 text-left transition-colors cursor-pointer hover:bg-primary-100/60"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge tone={auditImpactTone(event.impact)}>{event.impact}</Badge>
                    <span className="text-[11px] tabular-nums text-foreground-400">
                      {dayKeyOf(event.timestamp).slice(5)} · {clockOf(event.timestamp)}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm leading-snug text-foreground-800">
                    <span className="font-medium text-foreground-950">{event.actorName}</span>{' '}
                    {event.summary}
                  </p>
                  <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-foreground-500">
                    <Badge tone={auditCategoryTone(event.category)}>{event.category}</Badge>
                    {event.reference}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}